import type {Component, Editor} from 'grapesjs';
import {escapeHTML, xssCleanHtml} from '@Utils/string.util';
import {getAllComponentsInTree} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

const CONTENTEDITABLE_SELECTOR = '[contenteditable="true"]';

const DISALLOWED_TAGS: readonly string[] = [
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'link',
  'style',
  'meta',
  'base',
  'frame',
  'frameset',
  'applet',
  'canvas',
  'noscript',
  'svg',
  'audio',
  'video',
  'source',
  'track',
];

const HARMFUL_ATTRIBUTE_PREFIX = 'on';
const HARMFUL_SRCDOC_ATTRIBUTE = 'srcdoc';

/** A throwing node is logged and skipped rather than aborting the rest of the tree. */
export function sanitizeComponentContent(component: Component | undefined): void {
  if (!component) {
    return;
  }

  try {
    sanitizeComponent(component);
  } catch (e: unknown) {
    logEmailMakerError('sanitizeComponentContent: sanitization threw for component', e);
  }
}

export function sanitizeAllComponents(editor: Editor): void {
  const wrapper = editor.DomComponents.getWrapper();
  if (!wrapper) {
    return;
  }

  const components = getAllComponentsInTree(wrapper);

  // Re-sanitizes the whole flattened tree per component; removal-during-iteration relies on this, so don't collapse to a single pass.
  for (const component of components) {
    sanitizeComponentContent(component);
  }
}

/** Forces plain-text paste inside RTE-editable canvas elements. Returns a teardown. */
export function attachPasteAsTextListener(editor: Editor): () => void {
  const iframeBody: HTMLElement = editor.Canvas.getBody();

  const onPaste = (e: ClipboardEvent): void => {
    if (!(e.target instanceof Element) || !e.target.closest(CONTENTEDITABLE_SELECTOR)) {
      return;
    }

    e.stopPropagation();
    e.preventDefault();

    const text = e.clipboardData?.getData('text');

    if (text) {
      const formattedData = escapeHTML(text).replace(/(\r\n|\n|\r)/g, '<br>');
      // insertHTML inside the GrapesJS RTE has no non-deprecated equivalent.
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      e.target.ownerDocument.execCommand('insertHTML', false, formattedData);

      const component = editor.getSelected();
      if (component) {
        editor.trigger('component:update', component);
      }
    }
  };

  iframeBody.addEventListener('paste', onPaste);

  return (): void => {
    iframeBody.removeEventListener('paste', onPaste);
  };
}

function sanitizeComponent(component: Component): void {
  const content = component.get('content');
  if (typeof content === 'string') {
    const cleanedContent = xssCleanHtml(content);
    if (cleanedContent !== content) {
      component.set('content', cleanedContent);
    }
  }

  const attributes = component.get('attributes') ?? {};
  const harmfulAttributes = Object.keys(attributes).filter((attr) => {
    return attr.toLowerCase().startsWith(HARMFUL_ATTRIBUTE_PREFIX) || attr.toLowerCase() === HARMFUL_SRCDOC_ATTRIBUTE;
  });
  harmfulAttributes.forEach((attr) => component.removeAttributes(attr));

  const tagName = component.get('tagName');
  if (tagName && DISALLOWED_TAGS.includes(tagName.toLowerCase())) {
    component.remove();
    return;
  }

  component.components().forEach((child) => {
    sanitizeComponent(child);
  });
}
